import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2, inject } from '@angular/core';
import { MatSortHeader } from '@angular/material/sort';

@Directive({
  selector: '[resizeColumn]',
  standalone: true
})
export class ResizeColumnDirective implements OnInit {
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly matSortHeader = inject(MatSortHeader, { self: true, optional: true });

  @Input() minWidth = 100;
  @Input() width: number;
  private startX: number;
  private startWidth: number;
  private isResizing = false;
  private resizer: HTMLElement;
  private table: HTMLElement;
  private column: number;

  constructor() {
    this.resizer = this.renderer.createElement('div');
    this.renderer.addClass(this.resizer, 'resizer');
    this.renderer.setStyle(this.resizer, 'width', '5px');
    this.renderer.setStyle(this.resizer, 'height', '100%');
    this.renderer.setStyle(this.resizer, 'position', 'absolute');
    this.renderer.setStyle(this.resizer, 'right', '0');
    this.renderer.setStyle(this.resizer, 'top', '0');
    this.renderer.setStyle(this.resizer, 'cursor', 'col-resize');
    this.renderer.setStyle(this.resizer, 'user-select', 'none');
    this.renderer.setStyle(this.resizer, 'z-index', '1000');

    this.renderer.appendChild(this.el.nativeElement, this.resizer);

    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');

    this.table = this.findAncestorByTagName(this.el.nativeElement, 'table');

    // Find column index
    if (this.table) {
      const headerRow = this.table.querySelector('tr');
      if (headerRow) {
        const headers = Array.from(headerRow.children);
        this.column = headers.indexOf(this.el.nativeElement);
      }
    }
  }

  ngOnInit() {
    // Set initial width if provided
    if (this.width) {
      this.setColumnWidth(this.width);
    }
  }

  private findAncestorByTagName(element: any, tagName: string): HTMLElement {
    while (element && element.tagName !== tagName.toUpperCase()) {
      element = element.parentElement;
    }
    return element;
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (event.target === this.resizer) {
      event.preventDefault();
      event.stopPropagation();
      if (this.matSortHeader) {
        this.matSortHeader.disabled = true;
      }

      this.isResizing = true;
      this.startX = event.pageX;
      this.startWidth = this.el.nativeElement.offsetWidth;
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (event.target === this.resizer) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (this.isResizing) {
      const width = this.startWidth + (event.pageX - this.startX);

      if (width >= this.minWidth) {
        this.setColumnWidth(width);
      }
    }
  };

  private setColumnWidth(width: number): void {
    // Set width on header cell
    this.renderer.setStyle(this.el.nativeElement, 'width', `${width}px`);
    this.renderer.setStyle(this.el.nativeElement, 'min-width', `${width}px`);

    // Set width on all cells in this column
    if (this.table && this.column >= 0) {
      const cells = Array.from(this.table.querySelectorAll(`td:nth-child(${this.column + 1})`));
      for (const cell of cells) {
        this.renderer.setStyle(cell, 'width', `${width}px`);
        this.renderer.setStyle(cell, 'min-width', `${width}px`);
      }
    }
  }

  private onMouseUp = (): void => {
    if (this.isResizing) {
      if (this.matSortHeader) {
        setTimeout(() => {
          this.matSortHeader.disabled = false;
        });
      }

      this.isResizing = false;
      document.body.style.userSelect = '';

      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    }
  };
} 